import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ArrowLeft, ArrowRight, CheckCircle} from '../../../icons';
import {Spinner} from '../../../Spinner';
import {LoadErrorState} from '../../../elements/LoadErrorState';
import {AlertsApi} from '../../../../api/alertsApi';
import {ClientTabs} from '../../ClientTabs';
import {
    ActiveAlertRow,
    AlertKpis,
    AlertSnoozeModal,
    AlertsToolbar,
    ResolvedAlertRow,
    SnoozedAlertRow,
    copyAlertPrompt,
    nextResolved,
    useAlertSnooze,
    useRecalculationRefresh,
    withResolvedRows,
} from '../../../alerts_center/shared';
import {bitacoraAlertsPath} from './clientAlertsPaths';
import {
    FILTER_ALL,
    alertsForFilter,
    alertsSummary,
    filterFromParam,
    splitAlerts,
} from './clientAlertsLogic';
import './ClientAlertsView.scss';

/**
 * Alertas del cliente (`/client/:id/alerts`) — card j61xJLod.
 *
 * El tile «Alertas vigentes» del Panel llevaba a la Bitácora del AI Booster, donde las alertas vienen
 * mezcladas con todo lo que pasó en las plataformas y una fila por cada vez que sonaron. Esta es la
 * vista «Ahora» del mockup de alertas (`dashbo-sacudon/estrategia/ux21-alertas.html`) acotada a UN
 * cliente: los problemas que están pasando hoy, desde cuándo, cuántas veces volvieron, y silenciar.
 *
 * Lee el MISMO endpoint que el popover del Listado (`active-alerts`), que sale del mismo servicio
 * que el número del tile: el tile, la pill del listado y esta pantalla cuentan lo mismo. «Mis
 * reglas» y «Crear una alerta» del mockup no están: necesitan un motor de umbral absoluto que no
 * existe todavía.
 *
 * El filtro vive en la URL (`?filtro=`) para que se pueda compartir y volver atrás lo deshaga.
 */
export function ClientAlertsView({clientId}) {
    const {t} = useTranslation();
    const [data, setData] = useState(null);
    // `null` = sin error; string = la carga falló (se compara `!== null`, patrón de LoadErrorState).
    const [loadError, setLoadError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const rawFilter = searchParams.get('filtro');
    const filter = filterFromParam(rawFilter);

    // Un `?filtro=` que no existe abre «Todas», y la URL tiene que decir lo mismo que la pantalla:
    // si no, un link compartido desde ahí arrastra un filtro fantasma (QA-pt-12). `replace` para
    // no dejar una entrada de historial por la corrección.
    useEffect(() => {
        if (rawFilter !== null && filterFromParam(rawFilter) !== rawFilter) {
            setSearchParams({}, {replace: true});
        }
    }, [rawFilter, setSearchParams]);

    // El cliente que la pantalla muestra AHORA. La ruta no remonta al cambiar de cliente (selector del
    // topbar), así que una respuesta que llega tarde puede ser de uno anterior: si el GET de A tarda
    // más que el de B, sus filas quedarían pintadas bajo el nombre de B. Toda respuesta se descarta
    // si ya no es del cliente vigente (C-TARJETAS-02 del review).
    const currentClientRef = useRef(clientId);

    // `refresh` = recargar DESPUÉS de silenciar o reactivar, con la lista ya en pantalla. Si falla, la
    // lista que se ve sigue siendo válida: se avisa con un toast y no se tapa la pantalla con el
    // error de carga, que es para cuando no hay nada que mostrar (C-TARJETAS-06). `silent` = el refresco
    // de fondo mientras una línea se recalcula: si falla, calla y conserva lo que había.
    //
    // Con el refresco de fondo hay pedidos que se cruzan: manda el ÚLTIMO que salió (`requestSeqRef`). Uno que salió
    // antes de «Silenciar» y vuelve después pisaba lo silenciado (review C-08).
    const requestSeqRef = useRef(0);
    const inFlightRef = useRef(0);
    const load = useCallback(({refresh = false, silent = false} = {}) => {
        const requestedClientId = clientId;
        const seq = ++requestSeqRef.current;
        inFlightRef.current += 1;
        return AlertsApi.getActiveAlerts(requestedClientId).then((response) => {
            if (currentClientRef.current !== requestedClientId || seq !== requestSeqRef.current) return;
            if (!response || response.status !== 'OK') {
                if (silent) return;
                if (refresh) {
                    toast.error(t('client_alerts.refresh_error'));
                } else {
                    setLoadError(t('client_alerts.load_error'));
                }
                return;
            }
            setData(response.data);
            setLoadError(null);
        }).finally(() => {
            inFlightRef.current -= 1;
            if (currentClientRef.current === requestedClientId) setIsLoading(false);
        });
    }, [clientId, t]);

    // Las filas que se resolvieron mientras se miraba la pantalla (card Fnvibexi): quedan en su lugar como
    // «Resuelta» hasta salir. Se comparan las filas DIBUJADAS antes de la lectura (con las resueltas que ya había
    // y el filtro que se veía) con las vigentes de ésta: así el lugar de cada una es el que tenía en pantalla
    // (review C-09).
    const [resolved, setResolved] = useState(() => new Map());
    const previousShownRef = useRef([]);

    // Cambiar de cliente sin remontar: no mostrar las alertas del anterior mientras llegan las del
    // nuevo.
    useEffect(() => {
        currentClientRef.current = clientId;
        setData(null);
        setLoadError(null);
        setIsLoading(true);
        setResolved(new Map());
        previousShownRef.current = [];
        load();
        // `load` cambia con `t`, cuya identidad no es estable entre renders de i18next.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    const allActive = useMemo(() => splitAlerts(data).active, [data]);
    // Corre ANTES del efecto que guarda lo dibujado (orden de declaración): ve lo que estaba en pantalla.
    useEffect(() => {
        if (!data) return;
        const previousShown = previousShownRef.current;
        setResolved((current) => nextResolved(current, previousShown, data.recovered, allActive));
    }, [data, allActive]);

    // Mientras una línea se recalcula por un cambio guardado, la bandeja se refresca sola y callada. Con un pedido
    // en vuelo, el turno se saltea: no se apilan pedidos si el endpoint tarda (review C-08).
    const loadRef = useRef(load);
    loadRef.current = load;
    const backgroundRefresh = useCallback(() => {
        if (inFlightRef.current > 0) return undefined;
        return loadRef.current({silent: true});
    }, []);
    useRecalculationRefresh(allActive, backgroundRefresh);

    const retry = () => {
        setLoadError(null);
        setIsLoading(true);
        load();
    };

    const reload = useCallback(() => load({refresh: true}), [load]);
    const snooze = useAlertSnooze({reload});

    const summary = useMemo(() => alertsSummary(data), [data]);
    const rows = useMemo(() => alertsForFilter(data, filter), [data, filter]);
    // Una resuelta sale en el filtro donde salía su fila.
    const activeRows = useMemo(() => {
        const shown = [...resolved.values()].filter((entry) => alertsForFilter({items: [entry.item]}, filter)
            .active.length > 0);
        return withResolvedRows(rows.active, shown);
    }, [rows, resolved, filter]);
    // Lo que quedó dibujado: lo lee la próxima lectura para saber qué se resolvió y en qué lugar estaba.
    useEffect(() => {
        previousShownRef.current = activeRows.map((row) => row.item);
    }, [activeRows]);
    const snoozeable = useMemo(() => rows.active.filter((item) => item.snooze && item.snooze.available),
        [rows]);

    const changeFilter = (next) => {
        setSearchParams(next === FILTER_ALL ? {} : {filtro: next});
    };

    // ------------------------------------------------------------------ piezas ------------------
    // KPIs, chips y filas son las piezas compartidas con «Ahora» del Centro de Alertas
    // (`alerts_center/shared/`): se extrajeron de acá sin cambiar el dibujo.

    const renderActiveRow = (item, index) => (
        <ActiveAlertRow key={item.id ? `active-${item.id}` : `active-${index}`} item={item}
                        onSnooze={(row) => snooze.openSnooze(row)}
                        onCopyPrompt={(row) => copyAlertPrompt(row, clientId, t)}
                        onReactivate={(row) => snooze.reactivate(row)} isSaving={snooze.isSaving}/>
    );

    const renderSnoozedRow = (item, index) => (
        <SnoozedAlertRow key={`snoozed-${index}`} item={item} isSaving={snooze.isSaving}
                         onReactivate={(row) => snooze.reactivate(row)}/>
    );

    const renderActiveOrResolvedRow = ({item, resolved: resolvedRow}, index) => (resolvedRow
        ? <ResolvedAlertRow key={`resolved-${item.id}`} item={item} phrase={resolvedRow.phrase}/>
        : renderActiveRow(item, index));

    const renderList = () => {
        if (!activeRows.length && !rows.snoozed.length) {
            // Sin nada vigente es una buena noticia y se dice así; con un filtro prendido es sólo
            // que ese filtro no tiene filas.
            return (
                <div className="cav-empty">
                    {filter === FILTER_ALL && <CheckCircle size={22} weight="bold"/>}
                    {filter === FILTER_ALL ? t('client_alerts.empty_all') : t('client_alerts.empty_filter')}
                </div>
            );
        }
        return (
            <div className="cav-list">
                {activeRows.map(renderActiveOrResolvedRow)}
                {rows.snoozed.length > 0 && filter === FILTER_ALL && (
                    <div className="cav-group">{t('clients_v2.alerts_snoozed_group')}</div>
                )}
                {rows.snoozed.map(renderSnoozedRow)}
            </div>
        );
    };

    const renderBody = () => {
        if (loadError !== null) {
            return (
                <LoadErrorState
                    title={loadError}
                    text={t('client_alerts.load_error_explanation')}
                    retryLabel={t('client_alerts.load_error_retry')}
                    onRetry={retry}
                />
            );
        }
        if (isLoading && !data) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }
        return (
            <>
                <AlertKpis summary={summary}/>
                <AlertsToolbar filter={filter} summary={summary} onChangeFilter={changeFilter}
                               snoozeable={snoozeable}
                               onSnoozeAll={(rows) => snooze.openSnooze(null, rows)}/>
                {renderList()}
                <div className="cav-foot">
                    <span>{t('client_alerts.foot')}</span>
                    <Link className="cav-link"
                          to={bitacoraAlertsPath(clientId, (data && data.window_days) || 7)}>
                        {t('clients_v2.alerts_see_history')}
                        <ArrowRight size={12} weight="bold"/>
                    </Link>
                </div>
            </>
        );
    };

    return (
        <section className="section-content">
            <ClientTabs clientId={clientId}/>

            <div className="pnl-screen cav-screen">
                <Link className="cav-back" to={`/client/${clientId}`}>
                    <ArrowLeft size={14} weight="bold"/>
                    {t('client_alerts.back')}
                </Link>

                <header className="cav-head">
                    <h1 className="cav-title">{t('client_alerts.title')}</h1>
                    <div className="cav-sub">{t('client_alerts.subtitle')}</div>
                </header>

                {renderBody()}
            </div>

            <AlertSnoozeModal snooze={snooze}/>
        </section>
    );
}
